import { LogIn, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { useAppSelector } from "@/stores/hook";
import { cn } from "@/lib/utils";
import { useIsImpersonating } from "@/features/auth/impersonation/ImpersonationBanner";
import {
  useCheckInForMutation,
  useCheckInMutation,
  useCheckInState,
  useCheckOutForMutation,
  useCheckOutMutation,
} from "../api/useCheckIn";

function sinceLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sameDay = d.toDateString() === new Date().toDateString();
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    ...(sameDay ? {} : { day: "numeric", month: "short" }),
  });
}

/**
 * Header pill for people the feature applies to: green "Checked in" with a
 * menu to check out, or amber "Checked out · Check in" that checks in on
 * click. Renders nothing for everyone else.
 *
 * Inside a "Switch user" session presence is the person's own, so the pill is
 * read-only — EXCEPT for a Super Admin whose org allows "Check in for them"
 * (`state.onBehalf`): then it offers "Check in for <name>" / "Check out for
 * <name>", audited on the server as done by the admin.
 */
export default function CheckInStatusPill() {
  const isAuthed = !!useAppSelector((s) => s.auth.currentUser);
  const viewed = useAppSelector((s) => s.auth.currentUser?.user);
  const impersonating = useIsImpersonating();
  const { data: state } = useCheckInState(isAuthed);
  const checkIn = useCheckInMutation();
  const checkOut = useCheckOutMutation();
  const checkInFor = useCheckInForMutation();
  const checkOutFor = useCheckOutForMutation();

  if (!state?.applies) return null;
  const busy = checkIn.isPending || checkOut.isPending || checkInFor.isPending || checkOutFor.isPending;
  const viewedId = Number(viewed?.id);
  const viewedName: string = viewed?.name ?? "this user";
  const firstName = viewedName.split(" ")[0] || viewedName;
  const canInFor = impersonating && !!state.onBehalf?.canCheckIn && viewedId > 0;
  const canOutFor = impersonating && !!state.onBehalf?.canCheckOut && viewedId > 0;

  if (!state.checkedIn) {
    const offTitle = canInFor
      ? `Check ${viewedName} in on their behalf (recorded as done by you)`
      : impersonating
        ? state.onBehalf?.canCheckOut
          ? "Checking people in for them is off — a Super Admin can allow it in Settings → Manage → Check-in / Check-out"
          : "Viewing as this user — presence is their own"
        : "Check in to receive leads";
    return (
      <button
        type="button"
        data-testid="check-in-pill"
        data-state="out"
        disabled={busy || (impersonating && !canInFor)}
        onClick={() =>
          canInFor ? checkInFor.mutate({ userId: viewedId, name: viewedName }) : checkIn.mutate()
        }
        title={offTitle}
        className={cn(
          "flex h-8 items-center gap-1.5 rounded-full border border-[#e2c78a] bg-[#fff6e3] px-3 text-[12px] font-medium text-[#6b4700] transition-colors",
          "hover:bg-[#fff1d6] disabled:cursor-not-allowed disabled:opacity-70",
        )}
      >
        <span className="size-2 rounded-full bg-[#d99a1f]" />
        Checked out
        {(!impersonating || canInFor) && (
          <span className="ml-1 flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-[#6b4700] shadow-sm">
            <LogIn className="size-3" />
            {busy ? "…" : canInFor ? `Check in for ${firstName}` : "Check in"}
          </span>
        )}
      </button>
    );
  }

  const since = sinceLabel(state.checkedInAt);
  const pill = (
    <button
      type="button"
      data-testid="check-in-pill"
      data-state="in"
      disabled={busy || (impersonating && !canOutFor)}
      className={cn(
        "flex h-8 items-center gap-1.5 rounded-full border border-[var(--color-green-5)] bg-[var(--color-green-1)] px-3 text-[12px] font-medium text-[var(--color-success)] transition-colors",
        "hover:bg-[var(--color-green-2)] disabled:cursor-default disabled:opacity-80",
      )}
    >
      <span className="size-2 rounded-full bg-[var(--color-success)]" />
      {state.presence === "on_break" ? "On a break" : "Checked in"}
    </button>
  );
  if (impersonating && !canOutFor) return pill;
  if (impersonating) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{pill}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]">
          <DropdownMenuLabel className="text-[12px] font-normal text-[var(--color-text-secondary)]">
            {since ? `${viewedName} · checked in since ${since}` : `${viewedName} is checked in`}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => checkOutFor.mutate({ userId: viewedId, name: viewedName })}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Check out for {firstName}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{pill}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel className="text-[12px] font-normal text-[var(--color-text-secondary)]">
          {since ? `Checked in since ${since}` : "Checked in"}
          {state.inactivityMinutes
            ? ` · auto check-out after ${state.inactivityMinutes} min idle`
            : ""}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => checkOut.mutate()}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Check out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
